import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  Input,
  Option,
  Popup,
  Table,
  TableRow,
  TextArea,
  TopBar,
} from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createNewOffer,
  createNewOrder,
  Customer,
  CustomerArticle,
  CustomerProjectConfig,
  generateDropdownOptions,
  generateDropdownOptionsForLanguage,
  generateNotification,
  getActiveCustomerArticlesForCustomer,
  getHtmlTextContent,
  getOfferByOrderId,
  getOrderById,
  importArticlesFromXlsx,
  importOrderFromXlsx,
  Language,
  Offer,
  OfferedTask,
  OfferProvidedBy,
  OfferType,
  Office,
  Order,
  OrderState,
  PaymentCondition,
  Project,
  Task,
  updateOrder,
  updateOrderAndOffer,
  useData,
  WorkInstruction,
} from 'shared';
import { CustomerLocation } from 'shared/src/customerLocation/CustomerLocation.types';
import { NotificationType } from 'shared/src/notification/notification.types';
import {
  createNewProjectFromCustomerProject,
  getAllProjectsOriginatedFrom,
} from 'shared/src/project/Project.axios';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { SaveButtons } from '../../components/saveButtons/SaveButtons';
import { SoftLockedPopup } from '../../components/SoftLockedPopup';
import { useUser } from '../../components/UserContext';
import { WorkInstructionOverview } from '../../components/workInstructions/WorkInstructionOverview';
import { generateDropdownOptionsForBusinessArea } from '../../utils/businessArea/BusinessArea.utils';
import { preventSubmitOnEnter } from '../../utils/general.utils';
import {
  convertOfferedTasksIntoTableEntries,
  generateDropdownOptionsForCustomers,
  generateDropdownOptionsForCustomerUsers,
  generateEmptyOffer,
  generateEmptyOfferedTask,
  generateEmptyOrder,
} from '../../utils/order/Order.utils';

export const CustomerProjectCreateOrEdit: React.FC = () => {
  const navigate = useNavigate();
  const button = useRef<HTMLButtonElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const { axios, updateFootnoteConfig } = useUser();
  const location = useLocation<{ order?: Order; offer?: Offer; orderId?: string }>();
  const { t } = useTranslation();
  const uploadRef = useRef<HTMLInputElement>(null);
  const orderUploadRef = useRef<HTMLInputElement>(null);

  const [customerProject, setCustomerProject] = useState<Offer>(
    location.state?.offer ??
      generateEmptyOffer({
        offerType: OfferType.CUSTOMER_PROJECT,
        providedBy: OfferProvidedBy.CUSTOMER_PROJECT,
      }),
  );
  const [projectOrder, setProjectOrder] = useState<Order>(
    location.state?.order ?? generateEmptyOrder({ offerType: OfferType.CUSTOMER_PROJECT }),
  );
  const [offeredTaskIdToDelete, setOfferedTaskIdToDelete] = useState<string>();
  const [customerArticles, setCustomerArticles] = useState<CustomerArticle[]>([]);
  const [createConfig, setCreateConfig] = useState<CustomerProjectConfig>();
  const [missingTasks, setMissingTasks] = useState<string[]>([]);
  const [priceDifferencesMap, setPriceDifferencesMap] = useState<Map<string, number>>(
    new Map<string, number>(),
  );
  const [showOnlyAssignedTasks, toggleOnlyAssignedTasks] = useState<boolean>(false);
  const [showClosePopup, toggleClosePopup] = useState<boolean>(false);
  const [originatedProjects, setOriginatedProjects] = useState<Project[]>([]);
  /**
   * Hook to update the footNote containg the create and update data
   */
  useEffect(() => {
    updateFootnoteConfig(projectOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectOrder.createDate]);

  //If orderId is provided load order and offer
  useEffect(() => {
    const orderId: string | undefined = location.state?.orderId;
    if (!orderId) return;
    Promise.all([getOrderById(axios, orderId), getOfferByOrderId(axios, orderId)]).then(
      ([order, offer]) => {
        if (!offer || !order) return;
        setProjectOrder(order);
        setCustomerProject(offer);
      },
    );
  }, [location.state?.orderId]);

  //useEffect hook to get the customer specific articles
  useEffect(() => {
    if (!customerProject.customerId) return;
    getActiveCustomerArticlesForCustomer(axios, customerProject.customerId).then(
      setCustomerArticles,
    );
  }, [axios, customerProject.customerId]);

  useEffect(() => {
    if (!projectOrder.id) return;
    getAllProjectsOriginatedFrom(axios, projectOrder.id).then(setOriginatedProjects);
  }, [projectOrder.id]);

  const { data: loadedOffices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCurrencys } = useData('CURRENCY_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCompanies } = useData('COMPANY_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedBusinessAreas } = useData('BUSINESSAREA_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomers } = useData('CUSTOMER_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerLocations } = useData('CUSTOMER_LOCATION_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerUsers } = useData('CUSTOMER_USERS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedPaymentConditions } = useData('PAYMENT_CONDITION_ALL', {
    params: { fallbackData: [] },
  });

  const { data: loadedCustomerFeedbackEntries } = useData('FEEDBACK_ALL', {
    config: { fallbackData: [] },
  });

  /**
   * Holds the tasks and the customer specific articles as tasks
   */
  const availableTasks: Task[] = useMemo(
    (): Task[] =>
      customerArticles.map((task: CustomerArticle): Task => {
        const translation: Map<Language, string> = new Map<Language, string>();
        translation.set(customerProject.language, task.description || '');
        return {
          id: task.id,
          processingFee: false,
          amount: 1,
          annotation: task.annotation || '',
          translations: translation,
          identifier: task.partnumber || '',
          price: task.price || 1,
          standard: false,
          title: task.partnumber || '',
          unit: task.unit || '',
          disabled: false,
          unitCode: task.unitCode || '',
        };
      }),
    [customerArticles, customerProject.language],
  );

  /**
   * Holds all generated projects for the customerProject
   */
  const projectRows: TableRow[] = useMemo((): TableRow[] => {
    if (originatedProjects.length === 0) return [];
    return originatedProjects.map((project) => ({
      id: project.id,
      onClick: () => navigate('/project/edit', { state: { project } }),
      content: [
        project.numberRangeNumber,
        project.createDate.toLocaleDateString('DE-de'),
        t(`general.projectState.${project.projectState}`),
      ],
    }));
  }, [originatedProjects]);
  /**
   * Keeps rows with the customerFeedbackEntries
   */
  const feedbackRows: TableRow[] = useMemo((): TableRow[] => {
    if (!projectOrder) return [];
    return loadedCustomerFeedbackEntries.map((feedbackCategory) => ({
      id: feedbackCategory.id,
      content: [
        feedbackCategory.title,
        <Input
          minValue={1}
          maxValue={6}
          type="number"
          onChangeNumber={(value) => {
            const date: string = new Date().toISOString().split('T')[0];
            const newFeedbackMap: Map<string, Map<string, number>> = new Map<
              string,
              Map<string, number>
            >(projectOrder.customerFeedback);
            newFeedbackMap.get(date)?.set(date, value);
            setProjectOrder({
              ...projectOrder,
              customerFeedback: newFeedbackMap,
            });
          }}
          value={projectOrder.customerFeedback
            .get(new Date().toISOString().split('T')[0])
            ?.get(feedbackCategory.id)}
        />,
      ],
    }));
  }, [loadedCustomerFeedbackEntries, projectOrder]);

  //Holds generated TableRows out of offeredTasks
  const rows: TableRow[] = useMemo((): TableRow[] => {
    const customer: Customer | undefined = loadedCustomers.find(
      (cust) => cust.id === customerProject.customerId,
    );
    if (!customer) return [];
    const allTasks: Task[] = [
      ...availableTasks,
      ...customerProject.offeredTasks
        .map((offeredTask) => offeredTask.task)
        .filter((task) => !availableTasks.some((availableTask) => task.id === availableTask.id)),
    ];
    return convertOfferedTasksIntoTableEntries(
      customerProject.offeredTasks,
      allTasks,
      projectOrder.officeId,
      projectOrder.currencyId,
      customer,
      customerProject.language,
      setOfferedTaskIdToDelete,
      setCustomerProject,
      false,
      true,
    );
  }, [
    availableTasks,
    customerProject.customerId,
    customerProject.offeredTasks,
    customerProject.offeredTasks.length,
    loadedCustomers,
    projectOrder.currencyId,
    projectOrder.officeId,
  ]);

  /**
   * Submit handler to create or update the order and offer
   * @param redirect decides if page should navigate to overview after success
   */
  const handleSubmit = (redirect: boolean): void => {
    button.current?.click();
    if (!form.current?.checkValidity()) return;
    if (customerProject.id) {
      updateOrderAndOffer(axios, customerProject, projectOrder).then((updatedObjects) => {
        if (updatedObjects?.order && updatedObjects?.offer && redirect)
          navigate('/customer/project');
        else if (updatedObjects) {
          setCustomerProject(updatedObjects.offer);
          setProjectOrder(updatedObjects.order);
          generateNotification({
            type: NotificationType.SUCCESS,
            value: t('general.notification.success.offerUpdate'),
          });
        }
      });
    } else {
      createNewOrder(axios, projectOrder).then((createdOrder) => {
        if (!createdOrder) return;
        createNewOffer(axios, {
          ...customerProject,
          orderId: createdOrder.id,
        }).then((offer) => {
          if (offer && redirect) navigate('/customer/project');
          else if (offer) {
            setCustomerProject(offer);
            setProjectOrder(createdOrder);
            generateNotification({
              type: NotificationType.SUCCESS,
              value: t('general.notification.success.offerCreate'),
            });
          }
        });
      });
    }
  };

  const [softLockedCustomer, setSoftLockedCustomer] = useState<Customer>();

  /**
   * Helper to update all relevant locations after selecting the central customer
   *
   * @param customerId The selected customer id
   */
  const handleCentralCustomerSelection = (customerId: string): void => {
    const foundCustomer: Customer | undefined = loadedCustomers.find(
      (cust) => cust.id === customerId,
    );
    if (foundCustomer?.softLocked) {
      setSoftLockedCustomer(foundCustomer);
    }
    if (!foundCustomer) return;
    setProjectOrder({
      ...projectOrder,
      customerId,
      workingLocation: {
        customerId,
        customerLocationId: '',
        customerContactId: '',
      },
      invoiceRecipient: {
        customerUserId: '',
        customerLocationId: '',
        customerId,
      },
      paymentCondition: foundCustomer.paymentCondition,
    });
    setCustomerProject({
      ...customerProject,
      customerId,
    });
  };

  /**
   * Holds the options for the invoice recipient locations
   */
  const invoiceLocationOptions: Option[] = useMemo((): Option[] => {
    if (!projectOrder?.invoiceRecipient.customerId) return [];
    const locations: CustomerLocation[] = loadedCustomerLocations.filter(
      (location) => location.customerId === projectOrder?.invoiceRecipient.customerId,
    );
    return generateDropdownOptions(locations, 'name', 'id');
  }, [loadedCustomerLocations, projectOrder?.invoiceRecipient.customerId]);

  /**
   * Helper to fill some fields in respect of the given office id
   *
   * @param officeId The selected id of the office
   */
  const handleOfficeSelection = (officeId: string): void => {
    const selectedOffice: Office | undefined = loadedOffices.find(
      (office) => office.id === officeId,
    );
    if (!selectedOffice) return;
    setProjectOrder({
      ...projectOrder,
      officeId,
      companyId: selectedOffice.companyId,
    });
  };

  /*
   * Helper method to find the selected paymentCondition and update the offer
   * @param id of the selected paymentCondition
   */
  const handlePaymentConditionChange = (id: string): void => {
    const condition: PaymentCondition | undefined = loadedPaymentConditions?.find(
      (condition) => condition.id === id,
    );
    if (condition) setProjectOrder({ ...projectOrder, paymentCondition: condition });
  };

  /**
   * DropdownOptions for the businessAreas
   */
  const businessAreaOptions: Option[] = useMemo(
    (): Option[] =>
      generateDropdownOptionsForBusinessArea(
        loadedBusinessAreas.filter((area) => area.companyId === projectOrder.companyId),
      ),
    [loadedBusinessAreas, projectOrder.companyId],
  );

  /**
   * location of the customer that is selected for the project
   */
  const customerLocations: CustomerLocation[] = useMemo(
    (): CustomerLocation[] =>
      loadedCustomerLocations.filter(
        (location) => location.customerId === customerProject.customerId,
      ),
    [customerProject.customerId, loadedCustomerLocations],
  );

  /**
   * Helper method to create a new project for a custoimerProject
   */
  const handleCreateProject = (): void => {
    createConfig &&
      createNewProjectFromCustomerProject(axios, createConfig).then(
        (createdProject) =>
          createdProject && navigate('/project/edit', { state: { project: createdProject } }),
      );
  };

  /**
   * Helper method to extract articles of an uploaded xlsx file an create tasks
   * @param file the uploaded file
   */
  const handleArticleUpload = async (file: File): Promise<void> => {
    const importedTasks: OfferedTask[] = await importArticlesFromXlsx(axios, file);
    setCustomerProject({ ...customerProject, offeredTasks: importedTasks });
  };

  /**
   * Helper to import the "Abruf" with a selection of defined tasks
   * @param file the uplaoded file
   */
  const handleOrderUpload = async (file: File): Promise<void> => {
    if (!createConfig) return;
    const importData: OfferedTask[] = await importOrderFromXlsx(axios, file);
    const foundTasks: OfferedTask[] = [];
    const localMissingTasks: string[] = [];
    const workingCopy: Map<string, number> = new Map<string, number>(priceDifferencesMap);
    importData.map((importedTask) => {
      const task: OfferedTask | undefined = customerProject.offeredTasks.find(
        (task) => task.task.identifier === importedTask.task.identifier,
      );
      if (task) {
        foundTasks.push(importedTask);
        if (task.task.price !== importedTask.task.price)
          workingCopy.set(
            importedTask.task.identifier,
            Math.round(importedTask.task.price - task.task.price * 100) / 100,
          );
      } else localMissingTasks.push(importedTask.task.identifier);
    });

    setPriceDifferencesMap(workingCopy);
    setCreateConfig({ ...createConfig, tasks: foundTasks });
    setMissingTasks(localMissingTasks);
  };

  /**
   * Helper method to update selected workInstructions
   * @param checked should the instruction be added or removed?
   * @param instruction the instruction to add or remove
   */
  const handleCheckInstruction = (checked: boolean, instruction: WorkInstruction): void => {
    if (!createConfig) return;
    if (checked)
      setCreateConfig({
        ...createConfig,
        instructions: [...createConfig.instructions, instruction],
      });
    else
      setCreateConfig({
        ...createConfig,
        instructions: createConfig.instructions.filter((entry) => entry.id !== instruction.id),
      });
  };

  /**
   * Holds the selectable articles in the project create popup
   */
  const articleConfigRows: TableRow[] = useMemo((): TableRow[] => {
    if (!createConfig) return [];
    return customerProject.offeredTasks
      .filter(
        (task) =>
          !showOnlyAssignedTasks ||
          createConfig.tasks.some(
            (includedTask) => includedTask.task.identifier === task.task.identifier,
          ),
      )
      .map((task) => {
        const configTask: OfferedTask | undefined = createConfig.tasks.find(
          (includedTask) => includedTask.task.identifier === task.task.identifier,
        );
        const updatedTasks: OfferedTask[] = createConfig.tasks.some(
          (includedTask) => includedTask.id === task.id,
        )
          ? createConfig.tasks.filter((filterTask) => filterTask.id !== task.id)
          : [...createConfig.tasks, task];
        return {
          id: task.id,
          content: [
            <Checkbox
              isChecked={!!configTask}
              onCheck={() => setCreateConfig({ ...createConfig, tasks: updatedTasks })}
            />,
            task.task.identifier,
            task.task?.translations?.get(customerProject.language) || '-',
            configTask?.amount || task.amount,
          ],
        };
      });
  }, [createConfig, availableTasks, showOnlyAssignedTasks, customerProject]);

  return (
    <form ref={form} onSubmit={(evt) => evt.preventDefault()} onKeyDown={preventSubmitOnEnter}>
      <TopBar onBackClick={() => navigate(-1)} title="Projekt-Bestellung">
        {customerProject.id && projectOrder.orderState !== OrderState.PROJECT_COMPLETED && (
          <>
            <Button
              value={t('general.buttons.closeCustomerProject')}
              backgroundColor="#bc2e46"
              onClick={() => toggleClosePopup(true)}
            />
            <Button
              value={t('general.buttons.createCustomerProject')}
              onClick={() =>
                setCreateConfig({
                  offerId: customerProject.id,
                  customerOrderNumber: '',
                  plannedWorkers: 1,
                  tasks: [],
                  instructions: [],
                })
              }
            />
          </>
        )}
        <SaveButtons
          disabled={projectOrder.orderState === OrderState.PROJECT_COMPLETED}
          handleSubmit={handleSubmit}
          buttonRef={button}
        />
      </TopBar>
      {showClosePopup && (
        <Popup
          buttons={[
            <Button value={t('general.buttons.cancel')} onClick={() => toggleClosePopup(false)} />,
            <Button
              value={t('general.buttons.yes')}
              onClick={() =>
                updateOrder(axios, {
                  ...projectOrder,
                  orderState: OrderState.PROJECT_COMPLETED,
                }).then((updatedOrder) => updatedOrder && navigate(-1))
              }
            />,
          ]}
          isOpen={showClosePopup}
          children={<p>{t('pages.customerProject.edit.closePopupText')}</p>}
          title={t('pages.customerProject.edit.closePopupTitle')}
        />
      )}
      {createConfig && (
        <Popup
          buttons={[
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => setCreateConfig(undefined)}
            />,
            <Button value={t('general.buttons.create')} onClick={handleCreateProject} />,
          ]}
          isOpen={!!createConfig}
          onClose={() => setCreateConfig(undefined)}
        >
          <div>
            <Input
              type="text"
              value={createConfig?.customerOrderNumber}
              label={t('pages.customerProject.edit.createPopup.customerOrderNumber')}
              onChange={(customerOrderNumber) =>
                setCreateConfig({ ...createConfig, customerOrderNumber })
              }
            />
            <Input
              type="number"
              onChangeNumber={(plannedWorkers) =>
                setCreateConfig({ ...createConfig, plannedWorkers })
              }
              value={createConfig.plannedWorkers}
              label={t('pages.customerProject.edit.createPopup.plannedWorkers')}
            />
          </div>
          <div className="customer-project__project-popup__instruction-wrapper">
            <p className="customer-project__project-popup__instruction-wrapper__title">
              {t('pages.customerProject.edit.createPopup.instructionTitle')}
            </p>
            <div className="customer-project__project-popup__instruction-wrapper__instructions">
              {customerProject.workInstructions?.map((instruction) => (
                <Checkbox
                  isChecked={createConfig.instructions.includes(instruction)}
                  label={getHtmlTextContent(instruction.content).slice(0, 10) + '...'}
                  onCheck={(checked) => handleCheckInstruction(checked, instruction)}
                />
              ))}
            </div>
          </div>
          <div className="customer-project__project-popup__import-check">
            {missingTasks.length > 0 && (
              <div className="customer-project__project-popup__import-check__check-entry">
                <p>{t('pages.customerProject.edit.createPopup.missingTasks')}</p>
                <div>
                  {missingTasks.map((missingTask) => (
                    <p className="customer-project__project-popup__import-check__check-entry__article">
                      {missingTask}
                    </p>
                  ))}
                </div>
              </div>
            )}
            {Array.from(priceDifferencesMap).length > 0 && (
              <div className="customer-project__project-popup__import-check__check-entry">
                <p>{t('pages.customerProject.edit.createPopup.priceDifferences')}</p>
                <div>
                  {Array.from(priceDifferencesMap).map(([articleNumber, difference]) => (
                    <p className="customer-project__project-popup__import-check__check-entry__article">
                      {`${articleNumber}: `}
                      <span style={{ color: difference < 0 ? '#bc2e46' : '' }}>{difference}</span>
                      {`${loadedCurrencys.find((currency) => currency.id === projectOrder.currencyId)?.symbol}`}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="customer-project__project-popup__actions">
            <Checkbox
              label={t('pages.customerProject.edit.createPopup.toggleFilter')}
              isChecked={showOnlyAssignedTasks}
              onCheck={() => toggleOnlyAssignedTasks((old) => !old)}
            />
            <IconComponent
              className="customer-project__project-popup__actions__import-icon"
              type={IconType.UPLOAD}
              width={30}
              onClick={() => orderUploadRef.current?.click()}
            />
            <input
              type="file"
              accept={['.xlsx', '.xls'].join(',')}
              style={{ display: 'none' }}
              ref={orderUploadRef}
              onChange={(evt) => evt.target.files && handleOrderUpload(evt.target.files[0])}
            />
          </div>

          <Table
            header={
              t('pages.customerProject.edit.createPopup.tableHeader', {
                returnObjects: true,
              }) as TableHeader[]
            }
            rows={articleConfigRows}
          />
        </Popup>
      )}
      <SoftLockedPopup
        customer={softLockedCustomer}
        closePopup={() => setSoftLockedCustomer(undefined)}
      />
      <Popup
        isOpen={!!offeredTaskIdToDelete}
        onClose={() => setOfferedTaskIdToDelete(undefined)}
        title={t('pages.order.create.deleteTaskPopup')}
        buttons={[
          <Button
            backgroundColor="#BC2E46"
            value={t('general.buttons.yes')}
            onClick={() => {
              setCustomerProject((prev) => ({
                ...prev,
                offeredTasks: customerProject.offeredTasks.filter(
                  (task) => task.id !== offeredTaskIdToDelete,
                ),
              }));
              setOfferedTaskIdToDelete(undefined);
            }}
          />,
          <Button
            value={t('general.buttons.no')}
            onClick={() => setOfferedTaskIdToDelete(undefined)}
          />,
        ]}
      >
        <p>{t('pages.order.create.deleteTask')}</p>
      </Popup>
      <Box>
        <Input
          onChange={() => {}}
          type="text"
          disabled
          value={customerProject.numberRangeNumber || '-'}
          label={t('pages.order.create.numberRange')}
        />
        <fieldset className="three-columns">
          <legend>{t('pages.order.create.generalInformation')}</legend>
          <Dropdown
            selectedOption={projectOrder.officeId}
            onChange={handleOfficeSelection}
            label={t('pages.order.create.office')}
            options={loadedOffices.map((office) => ({
              label: office.name,
              value: office.id,
            }))}
          />
          <Dropdown
            label={t('pages.order.create.currency')}
            selectedOption={projectOrder.currencyId}
            onChange={(currencyId) => setProjectOrder({ ...projectOrder, currencyId })}
            options={generateDropdownOptions(loadedCurrencys, 'title', 'id')}
          />
          <Dropdown
            options={[
              {
                label: t(`pages.order.create.providedBy.${OfferProvidedBy.CUSTOMER_PROJECT}`),
                value: OfferProvidedBy.CUSTOMER_PROJECT,
              },
            ]}
            disabled
            label={t('pages.order.create.providedBy.label')}
            selectedOption={OfferProvidedBy.CUSTOMER_PROJECT}
            onChange={() => {}}
          />
          <Dropdown
            label={t('pages.order.create.language')}
            selectedOption={customerProject.language}
            onChange={(language) =>
              setCustomerProject({
                ...customerProject,
                language: language as Language,
              })
            }
            options={generateDropdownOptionsForLanguage()}
          />
          <Dropdown
            options={generateDropdownOptions(loadedCompanies, 'name', 'id')}
            selectedOption={projectOrder.companyId}
            onChange={(companyId) => setProjectOrder({ ...projectOrder, companyId })}
            label={t('pages.order.create.company')}
          />
          <Dropdown
            onChange={(businessAreaId) =>
              setProjectOrder({
                ...projectOrder,
                businessAreaId,
              })
            }
            options={businessAreaOptions}
            label={t('pages.order.create.businessArea')}
            selectedOption={projectOrder.businessAreaId}
          />
        </fieldset>
      </Box>
      <Box>
        <fieldset className="three-columns">
          <legend>{t('pages.order.create.customerInformation')}</legend>

          <Dropdown
            required
            onChange={handleCentralCustomerSelection}
            selectedOption={projectOrder.customerId}
            options={generateDropdownOptionsForCustomers(loadedCustomers)}
            label={t('pages.order.create.customer')}
          />
          <Dropdown
            required
            onChange={(locationId) => {
              setProjectOrder({ ...projectOrder, locationId });
            }}
            selectedOption={projectOrder.locationId}
            options={generateDropdownOptions(customerLocations, 'name', 'id')}
            label={t('pages.order.create.customerLocation')}
          />
          <Dropdown
            required
            selectedOption={projectOrder.customerContact}
            options={generateDropdownOptions(
              loadedCustomerUsers.filter(
                (customerUser) => customerUser.customerId === projectOrder.customerId,
              ),
              ['firstName', 'lastName'],
              'id',
            )}
            label={t('pages.order.create.contactPerson')}
            onChange={(customerContact) => setProjectOrder({ ...projectOrder, customerContact })}
          />
          <Dropdown
            required
            label={t('pages.order.create.workingLocationCustomer')}
            options={generateDropdownOptions(loadedCustomers, 'name', 'id')}
            onChange={(customerId) =>
              setProjectOrder({
                ...projectOrder,
                workingLocation: {
                  customerId,
                  customerLocationId: '',
                  customerContactId: '',
                },
              })
            }
            selectedOption={projectOrder?.workingLocation?.customerId || ''}
          />
          <Dropdown
            required
            label={t('pages.order.create.workingLocation')}
            options={generateDropdownOptions(
              loadedCustomerLocations.filter(
                (location) => location.customerId === projectOrder.workingLocation.customerId,
              ),
              'name',
              'id',
            )}
            onChange={(customerLocationId) =>
              setProjectOrder({
                ...projectOrder,
                workingLocation: {
                  ...projectOrder.workingLocation,
                  customerLocationId,
                },
              })
            }
            selectedOption={projectOrder?.workingLocation?.customerLocationId || ''}
          />
          <Dropdown
            required
            options={generateDropdownOptions(
              loadedCustomerUsers.filter(
                (custoemrUser) =>
                  custoemrUser.customerId === projectOrder.workingLocation.customerId,
              ),
              ['firstName', 'lastName'],
              'id',
            )}
            label={t('pages.order.create.workingLocationContact')}
            onChange={(selectedUserId) =>
              setProjectOrder({
                ...projectOrder,
                workingLocation: {
                  ...projectOrder.workingLocation,
                  customerContactId: selectedUserId,
                },
              })
            }
            selectedOption={projectOrder.workingLocation.customerContactId}
          />
          <Dropdown
            required
            selectedOption={projectOrder.invoiceRecipient?.customerId}
            label={t('pages.order.create.invoiceRecipient')}
            options={generateDropdownOptions(loadedCustomers, 'name', 'id')}
            onChange={(customerId) =>
              setProjectOrder({
                ...projectOrder,
                invoiceRecipient: {
                  customerUserId: '',
                  customerLocationId: '',
                  customerId,
                },
              })
            }
          />
          <Dropdown
            selectedOption={projectOrder.invoiceRecipient?.customerLocationId}
            label={t('pages.project.edit.invoiceRecipientLocation')}
            options={invoiceLocationOptions}
            onChange={(customerLocationId) =>
              setProjectOrder({
                ...projectOrder,
                invoiceRecipient: {
                  customerId: projectOrder.invoiceRecipient.customerId,
                  customerLocationId,
                  customerUserId: '',
                },
              })
            }
          />
          <Dropdown
            required
            disabled={!projectOrder.invoiceRecipient?.customerId}
            selectedOption={projectOrder.invoiceRecipient?.customerUserId}
            label={t('pages.order.create.invoiceContact')}
            options={generateDropdownOptionsForCustomerUsers(
              loadedCustomerUsers,
              projectOrder.invoiceRecipient.customerId,
            )}
            onChange={(customerUserId) => {
              if (projectOrder.invoiceRecipient)
                setProjectOrder({
                  ...projectOrder,
                  invoiceRecipient: {
                    ...projectOrder.invoiceRecipient,
                    customerUserId,
                  },
                });
            }}
          />
          <Dropdown
            required
            options={generateDropdownOptions(loadedPaymentConditions ?? [], 'description', 'id')}
            selectedOption={projectOrder.paymentCondition?.id}
            label={t('pages.order.create.paymentCondition')}
            onChange={handlePaymentConditionChange}
          />
          <TextArea
            label={t('pages.order.create.workingLocationInformation')}
            onChange={(workingLocationComment) =>
              setProjectOrder({ ...projectOrder, workingLocationComment })
            }
            value={projectOrder.workingLocationComment}
            resizable
          />

          <Dropdown
            selectedOption={projectOrder.additionalReceivers?.[0] || ''}
            options={generateDropdownOptions(
              loadedCustomerUsers.filter(
                (customerUser) => customerUser.customerId === projectOrder.customerId,
              ),
              ['firstName', 'lastName'],
              'id',
            )}
            label={t('pages.order.create.addReceiver')}
            onChangeMultiple={(additionalReceivers) =>
              setProjectOrder({ ...projectOrder, additionalReceivers })
            }
            multi
            create
            onCreateNew={(newReceiver) =>
              setProjectOrder({
                ...projectOrder,
                additionalReceivers: [
                  ...(projectOrder.additionalReceivers
                    ? [...projectOrder.additionalReceivers, newReceiver.value]
                    : newReceiver.value),
                ],
              })
            }
          />
          <Checkbox
            isChecked={!projectOrder.withoutInvoice}
            onCheck={() =>
              setProjectOrder({
                ...projectOrder,
                withoutInvoice: !projectOrder.withoutInvoice,
              })
            }
            label={t('pages.order.create.sendInvoice')}
          />
        </fieldset>
      </Box>
      {projectOrder.id && (
        <WorkInstructionOverview isReadOnly={false} order={projectOrder} offer={customerProject} />
      )}
      <Box>
        <fieldset>
          <legend>{t('pages.order.create.orderDescription')}</legend>
          <Input
            type="text"
            value={customerProject.annotation}
            onChange={(annotation) => setCustomerProject({ ...customerProject, annotation })}
            label={t('pages.order.create.description')}
          />
          <div>
            <div className="order-create--options-wrapper">
              {availableTasks.length === 0 ? (
                t('pages.order.create.noTasks')
              ) : (
                <IconComponent
                  type={IconType.ADD}
                  width={30}
                  onClick={() =>
                    setCustomerProject({
                      ...customerProject,
                      offeredTasks: [...customerProject.offeredTasks, generateEmptyOfferedTask()],
                    })
                  }
                />
              )}
              <IconComponent
                type={IconType.UPLOAD}
                width={30}
                onClick={() => uploadRef.current?.click()}
              />
              <input
                type="file"
                ref={uploadRef}
                style={{ display: 'none' }}
                accept={['.xlsx', '.xls'].join(',')}
                onChange={(e) =>
                  e.target.files &&
                  e.target.files.length > 0 &&
                  handleArticleUpload(e.target.files[0])
                }
              />
            </div>
            {rows && (
              <Table
                rows={rows}
                header={
                  t('pages.order.create.tableHeader', {
                    returnObjects: true,
                  }) as TableHeader[]
                }
              />
            )}
          </div>
        </fieldset>
      </Box>
      <Box title={'Generierte Aufträge'}>
        <Table
          rows={projectRows}
          header={
            t('pages.project.edit.projectsTableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
      <Box title={t('pages.project.edit.customerFeedback')}>
        <Table
          rows={feedbackRows}
          header={
            t('pages.project.edit.feedbackTableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </form>
  );
};
